import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to handle closing popups when clicking outside or pressing ESC key
 * 
 * @param {boolean} isOpen - Whether the popup is currently open
 * @param {function} onClose - Function to call to close the popup
 * @param {boolean} [closeOnClickOutside=false] - Whether to close the popup when clicking outside (default: false)
 * @param {boolean} [closeOnEscKey=true] - Whether to close the popup when pressing ESC key (default: true)
 * @param {boolean} [preventBackgroundScroll=true] - Whether to prevent background scrolling when popup is open (default: true)
 * @returns {React.RefObject} - Ref to attach to the popup container
 * 
 * @example
 * const PopupComponent = ({ isOpen, onClose }) => {
 *   // Default behavior: close on ESC key, prevent background scroll, but not close on click outside
 *   const popupRef = usePopupClose(isOpen, onClose);
 *   
 *   // To enable close on click outside:
 *   // const popupRef = usePopupClose(isOpen, onClose, true);
 *   
 *   // To disable close on ESC key:
 *   // const popupRef = usePopupClose(isOpen, onClose, false, false);
 *   
 *   // To disable background scroll prevention:
 *   // const popupRef = usePopupClose(isOpen, onClose, false, true, false);
 *   
 *   if (!isOpen) return null;
 *   
 *   return (
 *     <div className="overlay">
 *       <div ref={popupRef} className="popup-content">
 *         Popup content here
 *       </div>
 *     </div>
 *   );
 * };
 */
export const usePopupClose = (isOpen, onClose, closeOnClickOutside = false, closeOnEscKey = true, preventBackgroundScroll = true) => {
  const popupRef = useRef(null);

  // Handle ESC key press
  const handleEscKey = useCallback((e) => {
    if (e.key === 'Escape' && isOpen && closeOnEscKey) {
      onClose();
    }
  }, [isOpen, onClose, closeOnEscKey]);

  // Handle click outside
  const handleClickOutside = useCallback((e) => {
    if (popupRef.current && !popupRef.current.contains(e.target) && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  // Prevent background scroll when popup is open
  useEffect(() => {
    if (isOpen && preventBackgroundScroll) {
      // Save current scroll position and prevent scrolling
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll position and allow scrolling
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, preventBackgroundScroll]);

  // Add event listeners
  useEffect(() => {
    if (isOpen) {
      // Only add click outside listener if closeOnClickOutside is true
      if (closeOnClickOutside) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      // Only add ESC key listener if closeOnEscKey is true
      if (closeOnEscKey) {
        document.addEventListener('keydown', handleEscKey);
      }
    }
    return () => {
      if (closeOnClickOutside) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
      if (closeOnEscKey) {
        document.removeEventListener('keydown', handleEscKey);
      }
    };
  }, [isOpen, handleClickOutside, handleEscKey, closeOnClickOutside, closeOnEscKey]);

  return popupRef;
};

export default usePopupClose;
